import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { getDirectReports, offboardEmployee } from "@/api/employees";
import { previewCobraForOffboarding, type CobraQuote } from "@/api/cobra";
import { useToast } from "@/hooks/useToast";
import { AlertTriangle, ShieldAlert } from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName: string;
  onDone: () => void;
}

export function OffboardModal({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  onDone,
}: Props) {
  const { addToast } = useToast();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [reassignTo, setReassignTo] = useState("");
  const [deactivateUser, setDeactivateUser] = useState(true);
  const [reports, setReports] = useState<{ id: string; first_name: string; last_name: string }[]>(
    []
  );
  const [saving, setSaving] = useState(false);
  // COBRA pre-flight quote — re-fetched whenever the date or reason
  // changes so the admin always sees the math that will actually run.
  const [cobra, setCobra] = useState<CobraQuote | null>(null);
  const [cobraLoading, setCobraLoading] = useState(false);
  const [sendCobra, setSendCobra] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    getDirectReports(employeeId).then(setReports).catch(() => setReports([]));
  }, [isOpen, employeeId]);

  // Re-quote COBRA when the user changes the date/reason. We only
  // call when the modal is open AND a date is set; the request is
  // cheap (single SQL + a rule lookup) so we don't debounce.
  useEffect(() => {
    if (!isOpen || !date) return;
    let cancelled = false;
    setCobraLoading(true);
    previewCobraForOffboarding(employeeId, {
      event_date: date,
      termination_reason: reason || undefined,
    })
      .then((q) => {
        if (!cancelled) setCobra(q);
      })
      .catch(() => {
        if (!cancelled) setCobra(null);
      })
      .finally(() => {
        if (!cancelled) setCobraLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeId, date, reason]);

  const confirm = async () => {
    setSaving(true);
    try {
      await offboardEmployee(employeeId, {
        termination_date: date,
        reason: reason || undefined,
        notes: notes.trim() || undefined,
        reassign_reports_to: reassignTo || undefined,
        deactivate_user: deactivateUser,
        send_cobra_notice: sendCobra,
      });
      addToast("success", `${employeeName} offboarded`);
      onDone();
      onClose();
    } catch (err: unknown) {
      // Known intermittent symptom: the backend successfully terminates
      // the employee and commits the transaction, but the response
      // serialization or an axios-layer issue (CORS preflight retry,
      // network hiccup after 200) bubbles as an error on the client.
      // Refreshing on both success AND error paths guarantees the
      // profile view reflects the real DB state rather than leaving
      // HR staring at an "ACTIVE" badge on a terminated employee.
      //
      // We also log the full error shape so the real cause shows up in
      // console rather than a generic "Failed to offboard" toast.
      const ax = err as {
        response?: { status?: number; data?: { detail?: unknown } };
        message?: string;
      };
      const status = ax?.response?.status;
      const detail = ax?.response?.data?.detail;
      console.error("OffboardModal: offboard call failed", {
        status,
        detail,
        message: ax?.message,
        err,
      });

      // Treat 2xx that somehow landed here as a success. Treat 5xx as a
      // warn (likely post-commit serialization bug) and refresh so HR
      // sees the real state. Treat 4xx as a real, user-facing error.
      if (status && status >= 200 && status < 300) {
        addToast("success", `${employeeName} offboarded`);
        onDone();
        onClose();
      } else if (status && status >= 500) {
        addToast(
          "warning",
          `Offboard may have partially succeeded — refreshing to show the latest state.`,
        );
        onDone();
        onClose();
      } else {
        const msg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail) && detail[0]
              ? (detail[0] as { msg?: string }).msg || "Failed to offboard"
              : "Failed to offboard";
        addToast("error", msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Offboard ${employeeName}`}>
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-3">
          <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm text-text-primary">
            This will mark the employee as terminated, reassign direct reports, and
            (optionally) deactivate their login.
          </div>
        </div>
        <Input
          label="Termination date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Reason
          </label>
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            options={[
              { value: "", label: "— Select reason —" },
              { value: "resignation", label: "Resignation" },
              { value: "termination_for_cause", label: "Termination for cause" },
              { value: "layoff", label: "Layoff / RIF" },
              { value: "retirement", label: "Retirement" },
              { value: "contract_end", label: "Contract end" },
              { value: "mutual", label: "Mutual agreement" },
              { value: "other", label: "Other" },
            ]}
          />
        </div>
        {/* COBRA notice preview — re-quotes whenever date/reason
            changes. Shown for any reason that triggers continuation
            coverage; ineligible cases (no health plan on file, no
            jurisdictional rule match, etc.) get a softer informational
            line so the admin knows we evaluated. */}
        {(cobraLoading || cobra) && (
          <div className="rounded-lg border border-accent/30 bg-accent/5 p-3 text-sm">
            <div className="flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 text-accent mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-text-primary">
                  COBRA Notice {cobra?.eligible ? "Required" : "Check"}
                </div>
                {cobraLoading && (
                  <p className="text-xs text-text-muted mt-1">
                    Calculating coverage…
                  </p>
                )}
                {!cobraLoading && cobra?.eligible && (
                  <div className="text-xs text-text-muted mt-1 space-y-1">
                    {cobra.elections && cobra.elections.length > 0 ? (
                      <div>
                        <div className="text-text-primary">
                          {employeeName} is enrolled in:
                        </div>
                        <ul className="mt-1 space-y-0.5">
                          {cobra.elections.map((line, idx) => (
                            <li
                              key={line.plan_id || idx}
                              className="flex items-center gap-1.5 flex-wrap"
                            >
                              <span>•</span>
                              <span className="text-text-primary">
                                {line.plan_name || "(plan)"}
                              </span>
                              {line.tier && line.tier !== "waived" && (
                                <span>
                                  ({line.tier.replace(/_/g, " ")})
                                </span>
                              )}
                              <span>—</span>
                              <span className="text-text-primary tabular-nums">
                                ${line.monthly_premium.toFixed(2)}/mo
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div>
                        {cobra.plan_summary
                          ? `Enrolled in: ${cobra.plan_summary}`
                          : "Enrolled in employer-sponsored coverage"}
                      </div>
                    )}
                    <div>
                      {cobra.jurisdiction === "federal"
                        ? "Federal COBRA"
                        : cobra.jurisdiction
                          ? `State mini-COBRA (${cobra.jurisdiction})`
                          : "COBRA"}{" "}
                      applies (company has{" "}
                      {cobra.employer_employee_count} employees).
                    </div>
                    <div>
                      Total premium:{" "}
                      <span className="text-text-primary">
                        ${cobra.monthly_premium.toFixed(2)}
                      </span>
                      /month
                      {cobra.base_premium > 0 && (
                        <> (102% of ${cobra.base_premium.toFixed(2)})</>
                      )}
                    </div>
                    {cobra.election_deadline && (
                      <div>
                        Election deadline:{" "}
                        <span className="text-text-primary">
                          {new Date(cobra.election_deadline).toLocaleDateString()}
                        </span>{" "}
                        (60 days from notice)
                      </div>
                    )}
                  </div>
                )}
                {!cobraLoading && cobra && !cobra.eligible && (
                  <p className="text-xs text-text-muted mt-1">
                    {cobra.reason_if_ineligible ||
                      "Coverage info not on file — review manually before offboarding."}
                  </p>
                )}

                {cobra?.eligible && (
                  <div className="mt-3 space-y-1.5">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="radio"
                        checked={sendCobra}
                        onChange={() => setSendCobra(true)}
                        className="text-accent focus:ring-accent"
                      />
                      Send COBRA notice automatically
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="radio"
                        checked={!sendCobra}
                        onChange={() => setSendCobra(false)}
                        className="text-accent focus:ring-accent"
                      />
                      I&rsquo;ll handle COBRA separately
                    </label>
                    <p className="text-[11px] text-text-muted mt-1.5">
                      The notice will be added to {employeeName}&rsquo;s
                      My Documents page and emailed on their next
                      login. You can resend any time from the COBRA
                      dashboard.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            HR notes (optional — not visible to the employee)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Internal context — performance history, rehire eligibility, etc."
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
        {reports.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Reassign {reports.length} direct report
              {reports.length === 1 ? "" : "s"} to
            </label>
            <Select
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              options={[
                { value: "", label: "— Leave unassigned —" },
                ...reports.map((r) => ({
                  value: r.id,
                  label: `${r.first_name} ${r.last_name}`,
                })),
              ]}
            />
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={deactivateUser}
            onChange={(e) => setDeactivateUser(e.target.checked)}
            className="rounded border-border text-accent focus:ring-accent"
          />
          Deactivate login account
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={confirm} isLoading={saving}>
            Offboard employee
          </Button>
        </div>
      </div>
    </Modal>
  );
}
